/* Dashboard SPA entry. Boots auth, renders the tab shell, and dispatches each
 * tab to its feature module. The /[acct]/test env switch is read in ctx.ts. */
import { ACCT, MODE, api, toast } from './ctx.js';
import { initModal, revealSecret } from './modal.js';
import { setHandlers } from './store.js';
import { $, fval } from '../dom.js';

import * as overview from './tabs/overview.js';
import * as agents from './tabs/agents.js';
import * as products from './tabs/products.js';
import * as links from './tabs/links.js';
import * as payments from './tabs/payments.js';
import * as webhooks from './tabs/webhooks.js';
import * as developers from './tabs/developers.js';
import * as settlement from './tabs/settlement.js';
import * as settings from './tabs/settings.js';

export interface Me {
  account: string; mode: string; claimed?: boolean; authed?: boolean;
  name?: string | null; owner_email?: string | null; payout_wallet?: string | null;
}
export interface TabModule { render(panel: HTMLElement, me: Me): void | Promise<void> }

const TABS: [string, TabModule][] = [
  ['Overview', overview],
  ['Agents', agents],
  ['Products', products],
  ['Payment links', links],
  ['Payments', payments],
  ['Webhooks', webhooks],
  ['Developers', developers],
  ['Settlement', settlement],
  ['Settings', settings],
];
const MODULE: Record<string, TabModule> = Object.fromEntries(TABS);

let CUR = 'Overview';
let ME: Me | null = null;

// --- nav / labels ---
$('acctLabel').textContent = `/${ACCT}${MODE === 'test' ? '/test' : ''}`;
const pill = $('modePill');
pill.className = 'mode-pill ' + MODE;
$('modeText').textContent = MODE;
$('footNote').innerHTML = `${location.host}/${ACCT}${MODE === 'test' ? '/test' : ''} · <b>non-custodial · testnet · no real funds</b>`;

initModal();

// --- auth ---
function showAuth(kind: 'claim' | 'login'): void {
  $('appView').classList.add('hidden');
  $('authView').classList.remove('hidden');
  const c = $('claimForm'), l = $('loginForm');
  if (kind === 'claim') {
    c.classList.remove('hidden'); l.classList.add('hidden');
    $('authTitle').textContent = 'Claim this account';
    $('authSub').innerHTML = `Create the <b>${ACCT}/test</b> sandbox to get your keys.`;
    $('authAlt').innerHTML = `Already have a secret key? <button data-auth="login">Sign in</button>`;
  } else {
    c.classList.add('hidden'); l.classList.remove('hidden');
    $('authTitle').textContent = 'Sign in';
    $('authSub').innerHTML = `Enter your <b>ob_sk_test_</b> key for <b>${ACCT}/test</b>.`;
    $('authAlt').innerHTML = `Need to claim it? <button data-auth="claim">Create account</button>`;
  }
  document.querySelectorAll<HTMLButtonElement>('[data-auth]').forEach((b) => {
    b.onclick = () => showAuth((b.dataset.auth as 'claim' | 'login'));
  });
}

function liveStub(): void {
  $('authView').classList.remove('hidden');
  $('authTitle').textContent = 'Live mode is stubbed in v0';
  $('authSub').innerHTML = `Use the sandbox: <a style="color:var(--accent)" href="/${ACCT}/test">/${ACCT}/test</a>`;
  $('claimForm').classList.add('hidden');
}

($('claimForm') as HTMLFormElement).addEventListener('submit', async (e) => {
  e.preventDefault();
  const f = e.target as HTMLFormElement;
  const r = await api<{ secret_key?: string; error?: string }>('/account/claim', { method: 'POST', body: JSON.stringify({ name: fval(f, 'name'), email: fval(f, 'email') }) });
  if (r.ok && r.data.secret_key) {
    revealSecret('Your secret key', r.data.secret_key, 'This <b>ob_sk</b> key authenticates your server-side API calls. Store it in a secrets manager — it is not retrievable later.');
    void boot();
  } else toast(r.data.error || 'error');
});
($('loginForm') as HTMLFormElement).addEventListener('submit', async (e) => {
  e.preventDefault();
  const f = e.target as HTMLFormElement;
  const r = await api<{ error?: string }>('/account/session', { method: 'POST', body: JSON.stringify({ secret_key: fval(f, 'secret_key').trim() }) });
  if (r.ok) { toast('Signed in'); void boot(); } else toast(r.data.error || 'invalid key');
});

// --- app shell ---
function showApp(me: Me): void {
  ME = me;
  $('authView').classList.add('hidden');
  $('appView').classList.remove('hidden');
  $('navRight').innerHTML = `<a href="/directory">Directory</a><a href="/docs">Docs</a><button id="logoutBtn">Sign out</button>`;
  ($('logoutBtn') as HTMLButtonElement).onclick = async () => { await api('/account/logout', { method: 'POST' }); location.reload(); };

  const t = $('tabs');
  t.innerHTML = TABS.map(([name]) => `<button class="${name === CUR ? 'active' : ''}" data-tab="${name}">${name}</button>`).join('');
  t.querySelectorAll<HTMLButtonElement>('button').forEach((b) => {
    b.onclick = () => { CUR = b.dataset.tab as string; showApp(ME!); };
  });

  const panel = $('panel');
  panel.innerHTML = '<div class="empty">Loading…</div>';
  void MODULE[CUR]!.render(panel, ME);
}

function navigate(tab: string): void { CUR = tab; if (ME) showApp(ME); }
setHandlers({ navigate, refresh: () => void boot() });

async function boot(): Promise<void> {
  if (MODE === 'live') { liveStub(); return; }
  const me = await api<Me>('/account');
  if (!me.data.claimed) return showAuth('claim');
  if (!me.data.authed) return showAuth('login');
  showApp(me.data);
}

void boot();
